import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const ui = {
    errors: {
        writeOffExceedsOutstanding: 'The write-off can\'t be larger than the outstanding balance.',
        writeOffCategoryMustBeExpense: 'The write-off category must be an expense category.',
        excessCategoryMustBeIncome: 'The surplus category must be an income category.',
        categoryGone: 'The selected category no longer exists. Pick another category and try again.',
        destinationAccountGone: 'The selected destination account no longer exists. Pick another account and try again.',
        noFreeBalanceToReReserve: 'The account doesn\'t have free balance to re-reserve this amount. Add balance to the account first.',
        accountGone: 'The selected account no longer exists. Pick another account and try again.',
        nextInstallmentExceedsTotal: 'The next installment can\'t be larger than the contract total.',
        scheduleMustMatchOutstanding: 'The payment plan total must equal the outstanding balance.',
        remainingInstallmentsExceedSeries: 'The remaining installments entered exceed the financing\'s current series.',
        scheduleEntryMustBePositive: 'Each payment in the plan needs an amount greater than zero.',
        goalDepositsManagedInGoal: 'Goal deposits are managed from the goal screen.',
        reservaYieldManagedInReserva: 'This yield belongs to a pocket, so its amount, date and account can\'t be changed here. To fix the amount or delete it, remove the yield from the pocket history: the income goes with it.',
        convertToTransferUnsupported: 'Only a standalone income or expense in an account can become a transfer.',
        convertToTransferSameAccount: 'Pick an account other than the current one.',
        convertToTransferCounterpartMismatch: 'The other entry does not match this one (amount, account or type).',
        chooseWriteOffExpenseCategory: 'Pick the expense category for the write-off.',
        chooseExcessIncomeCategory: 'Pick the income category for the surplus.',
        savedAmountWithdrawnLater: 'This amount was already withdrawn later. Adjust the withdrawals before deleting this save.',
        loanAlreadyClosed: 'This receivable is already closed.',
        financingArchivedNoPaydown: 'This financing is archived and can\'t receive paydowns.',
        financingArchivedNoPartialPayment: 'This contract is archived and can\'t receive payments.',
        financingInstallmentGone: 'This installment no longer exists in this contract.',
        financingCurveRequiredToRecalibrate: 'Recalibration needs a declining-instalment (SAC) contract with a rate.',
        financingArchivedNoRecalibrate: 'Cannot adjust the projection of an archived contract.',
        financingInstallmentAlreadyPaid: 'This installment is already paid.',
        partialPaymentAmountInvalid: 'Enter an amount greater than zero for the partial payment.',
        partialPaymentNotPartial: 'That amount covers the whole installment — use the regular payment to settle it.',
        partialPaymentGone: 'This partial payment no longer exists.',
        partialPaymentInstallmentSettled: 'The installment was already settled. Undo its payment before deleting this partial one.',
        chargesOnPaidInstallment: 'Charges can only be added to an installment that is still open.',
        chargesNegative: 'Charges can\'t be negative.',
        installmentAmountInvalid: 'The final installment amount must be greater than zero.',
        installmentLeavePaidInvoice: 'Installments on an invoice that already has payments can\'t leave the card. Try "This and future" starting from an installment on an unpaid invoice.',
        installmentRebasePaidInvoice: 'This change would drag installments into or out of an invoice that already has payments. Use "Only this", or "This and future" starting from an installment on an unpaid invoice.',
        financingArchivedNoUndo: 'This financing is archived. Undoing paydowns would bring pending installments back into the flow.',
        writeOffDateRequired: 'Enter the write-off date.',
        loanDateRequired: 'Enter the date.',
        nextDueDateRequiredRegen: 'Enter the next due date to regenerate the installments.',
        nextDueDateRequired: 'Enter the next due date.',
        paymentDateRequired: 'Enter the payment date.',
        nextInstallmentRequired: 'Enter the next installment to track.',
        personNameRequired: 'Enter the person\'s name.',
        contractNameRequired: 'Enter the contract name.',
        paydownInstallmentAmountInvalid: 'Enter a valid installment amount for the paydown.',
        financingMonthlyAmountInvalid: 'Enter a valid monthly amount for the financing.',
        writeOffAmountInvalid: 'Enter a valid amount for the write-off.',
        loanAmountInvalid: 'Enter a valid amount.',
        paymentAmountInvalid: 'Enter a valid amount for the payment.',
        personNameTaken: 'A person with that name already exists.',
        subcategoryNameTaken: 'A subcategory with that name already exists in this category.',
        tagNameTaken: 'A tag with that name already exists.',
        paydownEntriesManagedInFinancing: 'Paydown entries are managed from the financing screen.',
        loanEntriesManagedInLoans: 'This transaction is managed from the Owed to me screen.',
        financingEntriesManagedInFinancing: 'Financing entries are managed from the financing screen.',
        financingLoanEntriesManaged: 'Financing and loan entries are managed from their own screens.',
        invoicePaymentManagedInInvoice: 'Invoice payments are undone from the invoice screen, with the payment\'s trash button.',
        noPendingInstallmentsToPayDown: 'There are no pending installments to pay down in this financing.',
        cannotDeleteSingleFutureOccurrence: 'Can\'t delete just this future occurrence.',
        paydownWouldRemoveDueInstallments: 'This paydown would remove installments that are already due or open.',
        advanceExceedsPendingTotal: 'The amount paid is more than the pending installments total. To close the contract, pay the installments instead.',
        cardGone: 'The selected card no longer exists. Pick another card and try again.',
        tagNameEmpty: 'The tag name can\'t be empty.',
        totalInstallmentsBelowNext: 'The total number of installments can\'t be lower than the next installment number.',
        totalInstallmentsLockedAfterFirst: 'The total number of installments can only change before the first payment or paydown.',
        installmentPlanNotConvertible: 'Installment plans can\'t be converted into a recurring transaction',
        financingInstallmentsManaged: 'Financing installments are managed from the financing screen.',
        personNotFound: 'Person not found. Select or create the person again.',
        renegotiationsAreHistory: 'Date renegotiations are history and can\'t be undone.',
        financingCategoryRequired: 'Select the financing\'s category.',
        sourceAccountRequired: 'Select the account the money left from.',
        financingPaymentAccountRequired: 'Select the financing\'s payment account.',
        receivingAccountRequired: 'Select the account that received the money.',
        existingReceiptOnlyOnExistingLoan: 'A payment from before the app is only possible when the money never left your account.',
        existingReceiptExceedsOutstanding: 'A receipt from before the app cannot exceed the outstanding balance — the excess is income and needs an account.',
        categoryRequired: 'Select at least one category.',
        loanLockedAfterFirstEntry: 'Amount, date, and account can only change before the first payment or write-off.',
        categoryDeleteOnlyArchivedLeft: 'The only other category of this type is archived. Reactivate it (or create another active one) before deleting this one.',
        categoryDeleteNeedsAnother: 'Create another category of this type before deleting this one.',
    },
    toasts: {
        mutationFailed: 'Something went wrong',
        mutationRefused: 'Couldn\'t complete that',
        tryAgain: 'Try again.',
        convertedToTransfer: 'Converted to transfer',
        accountCreated: 'Account created',
        accountSaved: 'Account saved',
        accountDeleted: 'Account deleted',
        accountReactivated: 'Account reactivated',
        cardCreated: 'Card created',
        cardSaved: 'Card saved',
        cardDeleted: 'Card deleted',
        cardArchived: 'Card archived',
        cardReactivated: 'Card reactivated',
        categoryCreated: 'Category created',
        categorySaved: 'Category saved',
        categoryDeleted: 'Category deleted',
        categoryArchived: 'Category archived',
        categoryReactivated: 'Category reactivated',
        categoriesUpdated: 'Categories updated',
        categoriesMerged: 'Categories merged',
        categoryDeleteFailed: 'Couldn\'t delete the category',
        subcategoryCreated: 'Subcategory created',
        subcategorySaved: 'Subcategory saved',
        subcategoryRemoved: 'Subcategory removed',
        transactionSaved: 'Transaction saved',
        transactionDeleted: 'Transaction deleted',
        undoAction: 'Undo',
        transactionRestored: 'Transaction restored',
        transactionsDeleted: 'Transactions deleted',
        transactionConfirmed: 'Transaction confirmed',
        markedPending: 'Marked as pending',
        transferRecorded: 'Transfer recorded',
        installmentPlanCreated: 'Installment plan created',
        installmentPlanDeleted: 'Installment plan deleted',
        budgetCreated: 'Budget created',
        budgetSaved: 'Budget saved',
        budgetDeleted: 'Budget deleted',
        goalCreated: 'Goal created',
        goalSaved: 'Goal saved',
        goalDeleted: 'Goal deleted',
        goalCompleted: 'Goal completed',
        goalReopened: 'Goal reopened',
        pocketCreated: 'Pocket created',
        pocketSaved: 'Pocket saved',
        pocketDeleted: 'Pocket deleted',
        pocketArchived: 'Pocket archived',
        pocketReactivated: 'Pocket reactivated',
        pocketReactivatedZeroed: 'Pocket reactivated at zero',
        pocketEmpty: 'Pocket has no balance',
        savedToPocket: 'Saved to pocket',
        withdrawnFromPocket: 'Withdrawn from pocket',
        noFreeBalanceToSave: 'No free balance to save',
        depositRecorded: 'Deposit recorded',
        depositRemoved: 'Deposit removed',
        yieldRecorded: 'Yield recorded',
        movementDeleted: 'Movement deleted',
        balanceAdjusted: 'Balance adjusted',
        recurringCreated: 'Recurring transaction created',
        recurringSaved: 'Recurring transaction saved',
        recurringDeleted: 'Recurring transaction deleted',
        recurringHidden: 'Recurring transaction hidden',
        recurringUnhidden: 'Recurring transaction shown again',
        recurringLinked: 'Recurring transaction linked',
        occurrenceRecorded: 'Occurrence recorded',
        reminderOn: 'Reminder on',
        reminderOff: 'Reminder off',
        financingCreated: 'Financing created',
        financingArchived: 'Financing archived',
        financingDeleted: 'Financing deleted',
        loanContractCreated: 'Loan created',
        loanContractArchived: 'Loan archived',
        loanContractDeleted: 'Loan deleted',
        contractUpdated: 'Contract updated',
        paydownRecorded: 'Paydown recorded',
        partialPaymentRecorded: 'Partial payment recorded',
        partialPaymentDeleted: 'Partial payment deleted',
        installmentChargesSaved: 'Charges updated',
        financingCurveRecalibrated: 'Projection adjusted to the bill amount.',
        paydownDeleted: 'Paydown deleted',
        interestFeesRecorded: 'Interest and fees recorded',
        adjustmentSaved: 'Manual adjustment saved',
        adjustmentRemoved: 'Manual adjustment removed',
        adjustmentUpdated: 'Adjustment updated',
        statusUpdated: 'Status updated',
        paymentRecorded: 'Payment recorded',
        paymentRecordedInvoiceStillOpen: 'Payment recorded. The statement stays open.',
        paymentRemoved: 'Payment removed',
        creditRecorded: 'Credit recorded',
        creditRolledToNext: 'Credit rolled to the next statement',
        balanceRolledToNext: 'Balance rolled to the next statement',
        adjustmentOnInvoice: 'Adjustment recorded on the statement',
        loanRecorded: 'Receivable recorded',
        loanUpdated: 'Receivable updated',
        loanDeleted: 'Receivable deleted',
        loanPaymentRecorded: 'Payment recorded',
        writeOffRecorded: 'Write-off recorded',
        scheduleUpdated: 'Payment plan updated',
        entryUndone: 'Entry undone',
        personRenamed: 'Person renamed',
        tagCreated: 'Tag created',
        tagUpdated: 'Tag updated',
        tagAdded: 'Tag added',
        tagsUpdated: 'Tags updated',
        tagDeleted: 'Tag deleted',
        ruleCreated: 'Rule created',
        ruleUpdated: 'Rule updated',
        ruleDeleted: 'Rule deleted',
        notificationDismissed: 'Notification dismissed',
        notificationsDismissed: 'Notifications dismissed',
        notificationRestored: 'Notification back in Pending',
        notificationsRestored: 'Notifications back in Pending',
        nothingToUpdate: 'Nothing to update',
        amountMustBePositive: 'Amount must be greater than 0',
        undoFailed: 'Couldn\'t undo',
        deleteFailed: 'Couldn\'t delete',
        saveFailed: 'Couldn\'t save',
        recordFailed: 'Couldn\'t record',
        renameFailed: 'Couldn\'t rename',
        reactivateFailed: 'Couldn\'t reactivate',
        cannotDelete: 'Can\'t delete',
        rulesReapplied_one: '{{count}} transaction reapplied',
        rulesReapplied_other: '{{count}} transactions reapplied',
        rulesFilled_one: '{{count}} transaction filled in',
        rulesFilled_other: '{{count}} transactions filled in',
    },
    actionSheet: {
        cancelLabel: 'Cancel',
    },
    confirmDialog: {
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
    },
    infoDialog: {
        okLabel: 'OK',
    },
    dateField: {
        placeholder: 'Select date',
    },
    dateTimePicker: {
        doneButton: 'Done',
        cancelButton: 'Cancel',
        okButton: 'OK',
    },
    headerBackButton: {
        backAccessibilityLabel: 'Back',
    },
    headerSearchButton: {
        openAccessibilityLabel: 'Search this list',
        closeAccessibilityLabel: 'Close search',
    },
    iconGroups: {
        money: 'Money',
        homeBills: 'Home & Bills',
        foodDrink: 'Food & Drink',
        transport: 'Transportation',
        shopping: 'Shopping',
        healthWellness: 'Health & Wellness',
        educationWork: 'Education & Work',
        leisureHobbies: 'Leisure & Hobbies',
        aiStreaming: 'AI & Streaming',
        travelNature: 'Travel & Nature',
        familyPets: 'Family & Pets',
        general: 'General',
    },
    iconPicker: {
        searchPlaceholder: 'Search icons...',
        emptyResults: 'No icons found',
    },
    lockScreen: {
        pinTitle: 'Enter your PIN',
        biometricSubtitle: 'Use {{biometric}} or enter your PIN',
        defaultSubtitle: 'To access Kontto',
        pinIncorrect: 'Incorrect PIN',
        pinValidationError: 'Failed to validate PIN',
    },
    pinInput: {
        verifying: 'Verifying...',
    },
    searchBar: {
        placeholder: 'Search...',
    },
    swipeableRow: {
        editAction: 'Edit',
        deleteAction: 'Delete',
        payAction: 'Pay',
        receiveAction: 'Receive',
        settleAction: 'Settle',
        pendingAction: 'Pending',
    },
    tabBar: {
        addAccessibilityLabel: 'Add',
    },
    deletedRecords: {
        account: 'Deleted account',
        card: 'Deleted card',
    },
} satisfies DeepPartialStrings<TranslationSchema['ui']>;
